import React, { useState, useEffect, useRef } from 'react';
import { X, User, MapPin, Lock, Save, Plus, Trash2 } from 'lucide-react';

function AccountModal({ isOpen, onClose, buyerInfo, setBuyerInfo }) {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    role: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France'
  });
  
  // Refs pour accéder directement aux inputs
  const nameRef = useRef(null);
  const companyRef = useRef(null);
  const phoneRef = useRef(null);
  const roleRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const postalCodeRef = useRef(null);
  const countryRef = useRef(null);
  
  const [addresses, setAddresses] = useState([]);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (buyerInfo) {
      setFormData({
        name: buyerInfo.name || '',
        firstName: buyerInfo.firstName || '',
        lastName: buyerInfo.lastName || '',
        company: buyerInfo.company || '',
        email: buyerInfo.email || '',
        phone: buyerInfo.phone || '',
        role: buyerInfo.role || '',
        address: buyerInfo.address || '',
        city: buyerInfo.city || '',
        postalCode: buyerInfo.postalCode || '',
        country: buyerInfo.country || 'France'
      });
      setAddresses(buyerInfo.savedAddresses || []);
    }
  }, [buyerInfo]);

  const handleSaveInfo = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    // Lire directement les valeurs des inputs via les refs
    const updatedData = {
      ...buyerInfo,
      name: nameRef.current?.value || formData.name,
      firstName: formData.firstName || (nameRef.current?.value || formData.name)?.split(' ')[0] || '',
      lastName: formData.lastName || (nameRef.current?.value || formData.name)?.split(' ').slice(1).join(' ') || '',
      company: companyRef.current?.value || formData.company,
      phone: phoneRef.current?.value || formData.phone,
      role: roleRef.current?.value || formData.role,
      address: addressRef.current?.value || formData.address,
      city: cityRef.current?.value || formData.city,
      postalCode: postalCodeRef.current?.value || formData.postalCode,
      country: countryRef.current?.value || formData.country
    };

    // Mettre à jour buyerInfo dans le localStorage immédiatement
    console.log('📝 Sauvegarde des données:', updatedData);
    setBuyerInfo(updatedData);

    const email = updatedData?.email || buyerInfo?.email || formData.email;
    if (!email) {
      setMessage({ type: 'error', text: 'Sauvegardé localement. Connectez-vous pour synchroniser votre compte.' });
      setIsSaving(false);
      return;
    }

    try {
      const { supabase } = await import('../utils/supabaseClient');
      
      const { data, error } = await supabase
        .from('clients')
        .update({
          name: updatedData.name,
          first_name: updatedData.firstName,
          last_name: updatedData.lastName,
          company: updatedData.company,
          phone: updatedData.phone,
          role: updatedData.role,
          address: updatedData.address,
          city: updatedData.city,
          postal_code: updatedData.postalCode,
          country: updatedData.country
        })
        .eq('email', email)
        .select('email');

      if (error) {
        console.warn('Supabase update error (clients):', error);
        setMessage({ type: 'error', text: 'Sauvegardé localement (Supabase non disponible)' });
      } else if (!data || data.length === 0) {
        setMessage({ type: 'error', text: 'Sauvegardé localement (compte introuvable sur Supabase)' });
      } else {
        setMessage({ type: 'success', text: 'Informations sauvegardées avec succès' });
      }
    } catch (err) {
      console.warn('Supabase update exception (clients):', err);
      setMessage({ type: 'error', text: 'Sauvegardé localement (Supabase non disponible)' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
      return;
    }

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { supabase } = await import('../utils/supabaseClient');
      
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        setMessage({ type: 'error', text: 'Erreur lors du changement de mot de passe' });
      } else {
        setMessage({ type: 'success', text: 'Mot de passe changé avec succès' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du changement de mot de passe' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAddress = () => {
    const newAddress = {
      firstName: formData.name?.split(' ')[0] || '',
      lastName: formData.name?.split(' ').slice(1).join(' ') || '',
      address: formData.address,
      city: formData.city,
      postalCode: formData.postalCode,
      country: formData.country
    };

    if (!newAddress.address || !newAddress.city || !newAddress.postalCode) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs obligatoires dans l\'onglet Informations' });
      setActiveTab('info');
      return;
    }

    const updatedAddresses = [...addresses, newAddress];
    setAddresses(updatedAddresses);

    try {
      (async () => {
        const { supabase } = await import('../utils/supabaseClient');
        const email = buyerInfo?.email || formData.email;
        if (!email) {
          setMessage({ type: 'error', text: 'Adresse ajoutée localement. Connectez-vous pour synchroniser.' });
          return;
        }
        
        const { data, error } = await supabase
          .from('clients')
          .update({ addresses: updatedAddresses })
          .eq('email', email)
          .select('email');

        if (error) {
          console.warn('Supabase update error (addresses):', error);
          setMessage({ type: 'error', text: 'Adresse ajoutée localement (Supabase non disponible)' });
          return;
        }

        if (!data || data.length === 0) {
          setMessage({ type: 'error', text: 'Adresse ajoutée localement (compte introuvable sur Supabase)' });
          return;
        }

        setMessage({ type: 'success', text: 'Adresse ajoutée avec succès' });
      })();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'ajout de l\'adresse' });
    }
  };

  const handleDeleteAddress = async (index) => {
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    setAddresses(updatedAddresses);

    try {
      const { supabase } = await import('../utils/supabaseClient');
      const email = buyerInfo?.email || formData.email;
      if (!email) {
        setMessage({ type: 'error', text: 'Adresse supprimée localement. Connectez-vous pour synchroniser.' });
        return;
      }
      
      const { data, error } = await supabase
        .from('clients')
        .update({ addresses: updatedAddresses })
        .eq('email', email)
        .select('email');

      if (error) {
        console.warn('Supabase update error (delete address):', error);
        setMessage({ type: 'error', text: 'Adresse supprimée localement (Supabase non disponible)' });
        return;
      }

      if (!data || data.length === 0) {
        setMessage({ type: 'error', text: 'Adresse supprimée localement (compte introuvable sur Supabase)' });
        return;
      }

      setMessage({ type: 'success', text: 'Adresse supprimée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-slate-800">
          <h2 className="text-2xl font-black text-slate-900 dark:text-white">Mon Compte</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b dark:border-slate-800">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-black uppercase tracking-wider transition-all ${
              activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <User className="w-4 h-4" />
            Informations
          </button>
          <button
            onClick={() => setActiveTab('addresses')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-black uppercase tracking-wider transition-all ${
              activeTab === 'addresses'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Adresses
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-black uppercase tracking-wider transition-all ${
              activeTab === 'password'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <Lock className="w-4 h-4" />
            Sécurité
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mx-6 mt-6 p-4 rounded-xl ${
            message.type === 'success' 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
          }`}>
            <p className="text-sm font-bold">{message.text}</p>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'info' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  ref={nameRef}
                  type="text"
                  placeholder="Nom complet *"
                  defaultValue={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  ref={companyRef}
                  type="text"
                  placeholder="Société *"
                  defaultValue={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  disabled
                  className="col-span-2 p-4 rounded-xl bg-slate-100 dark:bg-slate-700 outline-none opacity-60 cursor-not-allowed"
                />
                <input
                  ref={phoneRef}
                  type="text"
                  placeholder="Téléphone"
                  defaultValue={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  ref={roleRef}
                  type="text"
                  placeholder="Fonction"
                  defaultValue={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="pt-4 border-t dark:border-slate-800">
                <h3 className="text-sm font-black uppercase text-slate-500 mb-3">Adresse de facturation</h3>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    ref={addressRef}
                    type="text"
                    placeholder="Adresse *"
                    defaultValue={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="col-span-2 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    ref={cityRef}
                    type="text"
                    placeholder="Ville *"
                    defaultValue={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    ref={postalCodeRef}
                    type="text"
                    placeholder="Code Postal *"
                    defaultValue={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    ref={countryRef}
                    type="text"
                    placeholder="Pays"
                    defaultValue={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="col-span-2 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveInfo}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-wider hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          )}

          {activeTab === 'addresses' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black uppercase text-slate-500">Adresses enregistrées</h3>
                <button 
                  onClick={handleAddAddress}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase hover:bg-blue-700 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              </div>

              {addresses.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-bold">Aucune adresse enregistrée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {addresses.map((addr, index) => (
                    <div key={index} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-bold text-sm">{addr.firstName} {addr.lastName}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{addr.address}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{addr.postalCode} {addr.city}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteAddress(index)}
                        className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'password' && (
            <div className="space-y-4">
              <input
                type="password"
                placeholder="Mot de passe actuel"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="Confirmer le nouveau mot de passe"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full p-4 rounded-xl bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
              />

              <button
                onClick={handleChangePassword}
                disabled={isSaving || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-wider hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Lock className="w-5 h-5" />
                {isSaving ? 'Modification...' : 'Changer le mot de passe'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountModal;
